import { Injectable, effect, inject, signal } from '@angular/core';
import { firstValueFrom } from 'rxjs';
import { AlertController } from '@ionic/angular/standalone';
import { Capacitor } from '@capacitor/core';
import { environment } from '../../../environments/environment';
import { AppSettingsService } from './app-settings.service';
import { AuthService } from './auth.service';
import { EventLogService } from './event-log.service';
import { GpsService } from './gps.service';
import { JobsService } from './jobs.service';
import { NetworkService } from './network.service';
import { PlatformService } from './platform.service';
import { ReferenceDataService } from './reference-data.service';
import { VersionCheckService } from './version-check.service';
import { VersionCheckAlertService } from './version-check-alert.service';
import { UpqueueAdapter } from '../api/adapters/upqueue.adapter';
import { VERSION_CHECK_MESSAGES, VERSION_CHECK_HEADERS, VERSION_CHECK_BUTTONS } from '../constants/app.constants';

/**
 * Orchestrates all sync flows triggered by login, the refresh button, and the
 * auto-sync interval.
 *
 * @remarks
 * **Priority order per sync cycle:**
 * - Login/Manual refresh (`fullSync` / `manualRefresh`): P1 queue flush → P2 jobs → P3 ref data
 *   (jobs first so user sees job cards immediately on load)
 * - Auto-sync (`autoSync`): P1 queue flush → P2 ref data → P3 jobs (incremental)
 *   (ref data first; jobs called last with incremental timestamp)
 *
 * `AppSettingsService` is used for `startAutoSync()`'s `settings().refreshIntervalMs`
 * read and, since 2026-07-28, `manualRefresh()` also fires its `refresh()`
 * method (fire-and-forget) — see that method's doc for why.
 *
 * `JobsService.restoreCache()` is called before `fullSync()` on login so
 * stale cached data is visible while the network fetch is in-flight —
 * deliberately independent of `AppSettingsService.loaded` (2026-07-29 fix,
 * see the constructor `effect()`'s own doc comment for why).
 */
@Injectable({ providedIn: 'root' })
export class SyncService {
  private readonly authService         = inject(AuthService);
  private readonly appSettingsService  = inject(AppSettingsService);
  private readonly gpsService          = inject(GpsService);
  private readonly jobsService         = inject(JobsService);
  private readonly networkService      = inject(NetworkService);
  private readonly platform            = inject(PlatformService);
  private readonly refDataService      = inject(ReferenceDataService);
  private readonly upqueueAdapter      = inject(UpqueueAdapter);
  private readonly eventLogService     = inject(EventLogService);
  private readonly versionCheckService = inject(VersionCheckService);
  private readonly versionAlertService = inject(VersionCheckAlertService);
  private readonly alertCtrl           = inject(AlertController);

  /**
   * UTC Unix timestamp (seconds) of the last successful `loadJobs()` fetch.
   * `null` before the first successful sync or after logout.
   * Passed to `loadJobs()` during auto-sync for incremental diff fetches.
   *
   * @remarks
   * **Only ever advanced when `JobsService.loadJobs()` itself reports
   * success** (see `fullSync()`/`manualRefresh()`/`autoSync()` below) — never
   * unconditionally after the call. A prior version advanced this
   * unconditionally regardless of `loadJobs()`'s result: if the jobs request
   * failed, the timestamp still jumped to "now," so the next incremental
   * `autoSync()` request asked the server for changes only after that
   * failed-attempt timestamp — silently and permanently skipping any job
   * changes that occurred in the gap between the last successful sync and
   * the failed one, since a plain retry would never re-request that window.
   */
  readonly lastSyncTimestamp = signal<number | null>(null);

  /**
   * `true` while the login-triggered initial full sync is in flight —
   * i.e. from the moment `isAuthenticated()` flips `true` until `fullSync()`
   * (and `startAutoSync()`) has settled.
   *
   * @remarks
   * Drives `BrandHeaderComponent`'s refresh icon (2026-07-29) so it visibly
   * spins and is unclickable for the whole duration of the very first sync
   * after login — the officer otherwise had zero feedback that data was
   * being fetched at that point, since that same icon previously only
   * reacted to an explicit tap. Deliberately does **not** reflect
   * `manualRefresh()` (already has its own feedback via
   * `BrandHeaderComponent.refreshing`) or the background `autoSync()`
   * interval (deliberately silent, per this service's own
   * `flushQueues(true)` convention — a recurring spin every
   * `refreshIntervalMs` would be noisy, not informative). Reset to `false`
   * on logout as a safety net (should already be settled by then in any
   * normal flow).
   */
  readonly initialSyncInProgress = signal(false);

  private refreshTimer: ReturnType<typeof setInterval> | null = null;

  constructor() {
    effect(() => {
      if (this.authService.isAuthenticated()) {
        // Proactively request location permission on native so the OS dialog
        // appears at login rather than mid-flow when the user opens the camera.
        if (this.platform.isNative) {
          void this.gpsService.ensurePermission();
        }
        // restoreCache() is a local SecureStorage read — deliberately NOT
        // gated behind `appSettingsService.loaded` (2026-07-29 fix). It used
        // to be chained after `loaded` because that ordering was harmless at
        // the time: `AppSettingsService.init()` (an APP_INITIALIZER) blocked
        // Angular's entire first render until `loaded` had already resolved,
        // so by the time any component could mount, this whole chain had a
        // huge head start regardless. Once `init()` was changed to stop
        // blocking bootstrap on the network fetch (fixing a blank-screen-on-
        // reload report), `loaded` could now still be pending for however
        // long `GET /settings/app` takes — and since `restoreCache()` was
        // waiting on that same promise, cached job data (already on disk,
        // needing no network at all) stopped showing until the settings
        // fetch finished. Reported live: the Print Queue (and equally Jobs/
        // Accept/Sign) showed its empty state on reload despite having a
        // populated job cache, because `restoreCache()` hadn't run yet.
        // Fixed by calling `restoreCache()` immediately, independent of
        // `loaded` — only `fullSync()` (the actual network fetch, which
        // genuinely needs settings like `refreshIntervalMs`) still waits.
        this.initialSyncInProgress.set(true);
        void this.jobsService.restoreCache().then(() =>
          this.appSettingsService.loaded
            .then(() => this.fullSync())
            .then(() => this.startAutoSync())
            .finally(() => this.initialSyncInProgress.set(false)),
        );
      } else {
        this.stopAutoSync();
        this.lastSyncTimestamp.set(null);
        this.initialSyncInProgress.set(false);
      }
    });
  }

  // ─── Public sync entry points ──────────────────────────────────────────────

  /**
   * Full sync: called after login (via the `isAuthenticated` effect chain).
   *
   * @remarks
   * Runs the complete sync sequence without a timestamp so the server returns
   * the full job list. Updates {@link lastSyncTimestamp} only when
   * `loadJobs()` reports success — see that field's doc for why a failed
   * fetch must never advance it.
   *
   * Priority: P1 queue flush → P2 jobs (full fetch) → P3 reference data.
   * Jobs are fetched first so the user sees job cards immediately
   * without waiting for the slower lookup-table prefetch.
   *
   * Silently no-ops when the device is offline — API calls are skipped entirely
   * to prevent a flood of failed requests; the next sync cycle retries.
   *
   * @returns A promise that always resolves (individual step errors are swallowed).
   */
  async fullSync(): Promise<void> {
    const token = this.authService.token();
    if (!token) return;
    if (!this.networkService.isOnline()) return;

    await this.flushQueues(true);
    const jobsOk = await this.jobsService.loadJobs();
    await this.refDataService.prefetch(token.user.entityId);
    if (jobsOk) this.lastSyncTimestamp.set(this.utcUnixNow());
  }

  /**
   * Manual refresh: called by `BrandHeaderComponent` on refresh button tap.
   *
   * @remarks
   * Priority order: P1 queue flush → P2 jobs (full fetch) → P3 reference data.
   * Jobs are fetched before reference data so the user sees updated job cards
   * immediately without waiting for the slower lookup-table prefetch.
   *
   * Also fires `AppSettingsService.refresh()` (fire-and-forget — that method
   * already swallows its own errors and doesn't affect this method's return
   * value) so a manual refresh picks up a changed app setting (e.g.
   * `print_packet_threshold_web`) immediately, rather than only ever
   * refreshing once at boot and once right after login (2026-07-28). Neither
   * `fullSync()` (login) nor the background `autoSync()` interval do this —
   * only the explicit manual refresh does.
   *
   * Silently no-ops when the device is offline — the offline toast is shown by
   * `BrandHeaderComponent` before this method is reached.
   *
   * @returns `true` when at least one of jobs/reference-data actually refreshed
   *   successfully; `false` when there's no token, the device is offline, or
   *   both fetches failed — lets `BrandHeaderComponent` show an accurate
   *   success/failure toast instead of assuming success whenever the promise
   *   resolves (background sync's own error-swallowing is unaffected).
   */
  async manualRefresh(): Promise<boolean> {
    const token = this.authService.token();
    if (!token) return false;
    if (!this.networkService.isOnline()) return false;

    void this.appSettingsService.refresh();
    await this.flushQueues();
    const jobsOk = await this.jobsService.loadJobs();
    const refOk  = await this.refDataService.prefetch(token.user.entityId);
    if (jobsOk) this.lastSyncTimestamp.set(this.utcUnixNow());
    return jobsOk || refOk;
  }

  // ─── Private helpers ───────────────────────────────────────────────────────

  /**
   * Incremental sync: called by the auto-sync interval.
   *
   * @remarks
   * Identical to {@link fullSync} except `lastSyncTimestamp` is forwarded to
   * `loadJobs()` so the server returns only records changed since the last sync.
   * `lastSyncTimestamp` is only advanced when this `loadJobs()` call succeeds —
   * on failure it is left at its last successful value so the *next* tick
   * retries from the same point rather than silently skipping the gap (see
   * {@link lastSyncTimestamp}'s doc for the bug this fixes).
   * After syncing, checks if app version requires an update and shows an alert
   * if needed (native platform only).
   */
  private async autoSync(): Promise<void> {
    const token = this.authService.token();
    if (!token) return;
    if (!this.networkService.isOnline()) return;

    await this.flushQueues(true);
    await this.refDataService.prefetch(token.user.entityId);
    const jobsOk = await this.jobsService.loadJobs(this.lastSyncTimestamp() ?? undefined);
    if (jobsOk) this.lastSyncTimestamp.set(this.utcUnixNow());

    // Check for app updates during background sync on native platforms
    void this.checkSyncVersionUpdate();
  }

  /**
   * Starts the auto-sync interval using the configured `refreshIntervalMs`.
   * Clears any previously running timer before starting a new one.
   */
  private startAutoSync(): void {
    this.stopAutoSync();
    const intervalMs = this.appSettingsService.settings()?.refreshIntervalMs ?? 60_000;
    this.refreshTimer = setInterval(() => void this.autoSync(), intervalMs);
  }

  /** Stops the auto-sync interval if one is active. */
  private stopAutoSync(): void {
    if (this.refreshTimer !== null) {
      clearInterval(this.refreshTimer);
      this.refreshTimer = null;
    }
  }

  /**
   * Flushes all pending offline queues (upqueue submissions + event log entries)
   * before fetching reference data and jobs. Errors per queue are swallowed so
   * one failing queue never blocks the rest of the sync.
   *
   * @param background - When `true`, suppresses all upqueue result toasts so
   *   automatic sync cycles (login, auto-interval) do not interrupt the PS with
   *   unexpected alerts. Manual refresh passes `false` to show toasts normally.
   */
  private async flushQueues(background = false): Promise<void> {
    await Promise.allSettled([
      firstValueFrom(this.upqueueAdapter.transmitAll({ quiet: true, background })),
      this.eventLogService.flushPending(),
    ]);
  }

  /**
   * Checks if app version requires an update during background sync.
   * Shows an alert on native platforms if update is needed (force or soft).
   * Uses shared alert flag to prevent simultaneous alerts from other sources.
   *
   * @remarks
   * Called at the end of each `autoSync()` cycle. Fires on every sync tick
   * (every 60s) until the user updates the app.
   *
   * **Force update (critical):** Blocking alert with no cancel button.
   * User must tap "Update Now" to proceed.
   *
   * **Soft update (optional):** Dismissable alert with [Not Now] and [Update] buttons.
   */
  private async checkSyncVersionUpdate(): Promise<void> {
    if (!Capacitor.isNativePlatform()) return;

    if (this.versionAlertService.isAlertOpen()) return;

    const appVersion = this.appSettingsService.appVersion;
    const settings = this.appSettingsService.settings();
    if (!settings) return;

    const versionCheck = this.versionCheckService.checkForUpdate(appVersion, settings);
    if (!versionCheck.needsUpdate) return;

    this.versionAlertService.openAlert();

    const isCritical = versionCheck.isCritical;
    const message = isCritical
      ? VERSION_CHECK_MESSAGES.UPDATE_REQUIRED
      : VERSION_CHECK_MESSAGES.UPDATE_AVAILABLE;

    const buttons = isCritical
      ? [
          {
            text: VERSION_CHECK_BUTTONS.UPDATE,
            handler: () => {
              this.versionAlertService.closeAlert();
              this.openAppStore();
            },
          },
        ]
      : [
          {
            text: VERSION_CHECK_BUTTONS.NOT_NOW,
            handler: () => {
              this.versionAlertService.closeAlert();
            },
          },
          {
            text: VERSION_CHECK_BUTTONS.UPDATE,
            handler: () => {
              this.versionAlertService.closeAlert();
              this.openAppStore();
            },
          },
        ];

    const alert = await this.alertCtrl.create({
      header: VERSION_CHECK_HEADERS.WARNING,
      message,
      backdropDismiss: false,
      buttons,
    });
    await alert.present();
  }

  /** Opens the app store (iOS App Store or Google Play Store). */
  private openAppStore(): void {
    if (typeof window !== 'undefined') {
      const userAgent = window.navigator.userAgent.toLowerCase();

      if (userAgent.includes('iphone') || userAgent.includes('ipad')) {
        window.open(`itms-apps://itunes.apple.com/app/id${environment.iosAppId}`, '_system');
      } else if (userAgent.includes('android')) {
        window.open(`market://details?id=${environment.androidPackage}`, '_system');
      }
    }
  }

  /**
   * Returns the current UTC time as a Unix timestamp in seconds, matching the
   * server-expected format for `lastSyncTimestamp`.
   *
   * @remarks
   * Intentionally uses `toUTCString()` round-trip per the API contract spec:
   * `Math.floor(new Date(new Date().toUTCString()).getTime() / 1000)`.
   */
  private utcUnixNow(): number {
    const d = new Date();
    return Math.floor(new Date(d.toUTCString()).getTime() / 1000);
  }
}
